import { resolveClickTarget } from '../derivation/clickTarget';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { itemIdMatchesSource } from '../../../utils/itemIdPlane';

/** True for semantic / UUID ids that must not appear on a client Exo card. */
export function looksLikeKryspaceId(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (/^(co-|ai-|ca-|pt-|pf-|gqe-|pti-|wp-|imp-|rel-|prh-|prai-)/i.test(v)) return true;
  if (/^(person:|type:)/i.test(v)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return true;
  return false;
}

export function exoClientDisplayName(value: string | undefined): string | undefined {
  const v = value?.trim();
  if (!v || looksLikeKryspaceId(v)) return undefined;
  return v;
}

/** First sentence of activity_description — interim evidence until user_response_evidence. */
export function exoEvidenceLine(text: string | undefined): string | undefined {
  const raw = text?.trim();
  if (!raw) return undefined;
  const stripped = raw.replace(/^why we think so:\s*/i, '').trim();
  if (!stripped) return undefined;
  const sentence = stripped.match(/^[^.!?]+[.!?]?/);
  const line = (sentence?.[0] || stripped.split(/\n/)[0] || stripped).trim();
  return line || undefined;
}

/** Transition / action label for Mission Control next-move. Not the Exo hero action line. */
export function exoActionLabel(item: ControlBridgeQueueItem): string {
  if (item.activityTargetState) {
    const state = item.activityTargetState.replace(/_/g, ' ');
    return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
  }
  if (item.issueType) return 'Resolve issue';
  return 'Take action';
}

/** Client Exo action line — never a state-machine target name (T1 naming rule). */
export function exoHeroActionLine(item: ControlBridgeQueueItem): string {
  if (item.issueType) return 'Resolve issue';
  return '';
}

export interface ExoExecutionSuccessOptions extends ExoHeroContextOptions {
  /** Binary approval card — client ack is Approve, not Complete. */
  approvalCard?: boolean;
}

/** Post-action ack. Client copy never interpolates SM `toState`. */
export function exoExecutionSuccessMessage(
  action: 'complete' | 'defer' | 'block',
  toState?: string,
  options?: ExoExecutionSuccessOptions
): string {
  const showMachinery = Boolean(options?.showMachinery);
  const stateLabel = showMachinery && toState?.trim() ? toState.trim() : null;
  if (action === 'defer') {
    return stateLabel
      ? `Deferred — activity is now ${stateLabel}.`
      : 'Parked for later.';
  }
  if (action === 'block') {
    return stateLabel ? `Marked blocked — activity is now ${stateLabel}.` : 'Held.';
  }
  if (options?.approvalCard) {
    return stateLabel ? `Approved — activity is now ${stateLabel}.` : 'Approved.';
  }
  return stateLabel ? `Complete — activity is now ${stateLabel}.` : 'Saved.';
}

export function exoAssigneeLabel(item: ControlBridgeQueueItem): string | undefined {
  const label = item.assignedResourceLabel?.trim();
  if (label) return label;
  const ref = item.assignedResourceRef?.trim();
  if (ref) return ref.startsWith('person:') ? ref.slice(7) : ref;
  const res = item.res != null ? String(item.res).trim() : '';
  return res || undefined;
}

export interface ExoRecordContextModel {
  name?: string;
  customer?: string;
  value?: string;
  ageAtStep?: string;
  lastTouch?: string;
  owner?: string;
  trackedArtifactRef?: string;
}

/** Structured hero context for ExoPanel assigned-work closure (WBS seq 3) + Opgen A4. */
export interface ExoHeroContextModel {
  /** Client card: improvement display name only. Machinery: status · improvement · priority. */
  metaLine: string;
  assignee?: string;
  /** First sentence of activity_description (evidence). */
  evidence?: string;
  /** Full activity_description — machinery only. */
  description?: string;
  acceptanceHints: string[];
  blockedReason?: string;
  deferReason?: string;
  dependsOnRefs: string[];
  issueSummary?: string;
  /** Agent / Person when pickup_kind or resource signal exists — never invented from title. */
  actorKindLabel?: string;
  /** Display name from actorMarker.label (client: id-filtered). */
  actorName?: string;
  /** Name shown next to Actor/Person — marker label, else assignee; never the kind word. */
  actorDisplay?: string;
  effort?: string;
  waypointLabel?: string;
  /** Tracked-record chrome — preferred over activity description when present. */
  recordContext?: ExoRecordContextModel;
}

export interface ExoHeroContextOptions {
  /** Krysalis/admin: show ids, SM names, priority, ages, depends-on refs. */
  showMachinery?: boolean;
}

function exoRecordContext(item: ControlBridgeQueueItem): ExoRecordContextModel | undefined {
  const rc = item.recordContext;
  if (!rc) return undefined;
  const out: ExoRecordContextModel = {
    name: rc.name?.trim() || undefined,
    customer: rc.customer?.trim() || undefined,
    value: rc.value?.trim() || undefined,
    ageAtStep: rc.ageAtStep?.trim() || undefined,
    lastTouch: rc.lastTouch?.trim() || undefined,
    owner: rc.owner?.trim() || undefined,
    trackedArtifactRef: rc.trackedArtifactRef?.trim() || undefined,
  };
  if (!Object.values(out).some(Boolean)) return undefined;
  return out;
}

export function exoHeroContext(
  item: ControlBridgeQueueItem,
  options?: ExoHeroContextOptions
): ExoHeroContextModel {
  const showMachinery = Boolean(options?.showMachinery);
  const improvementDisplay = exoClientDisplayName(item.improvementTitle);
  const metaParts: string[] = [];
  if (showMachinery && item.statusPhrase?.trim()) metaParts.push(item.statusPhrase.trim());
  if (showMachinery && item.improvementTitle?.trim()) {
    metaParts.push(item.improvementTitle.trim());
  } else if (improvementDisplay) {
    metaParts.push(improvementDisplay);
  }
  if (showMachinery && item.priority?.trim()) metaParts.push(item.priority.trim());

  const recordContextRaw = exoRecordContext(item);
  let recordContext = recordContextRaw;
  if (recordContext && !showMachinery) {
    recordContext = {
      name: exoClientDisplayName(recordContext.name),
      customer: exoClientDisplayName(recordContext.customer),
      value: exoClientDisplayName(recordContext.value),
      owner: exoClientDisplayName(recordContext.owner),
    };
    if (!Object.values(recordContext).some(Boolean)) {
      recordContext = undefined;
    }
  }

  const evidence = exoEvidenceLine(item.activityDescription);
  const description = showMachinery ? item.activityDescription?.trim() || undefined : undefined;
  const acceptanceHints = (item.acceptanceHints ?? [])
    .map((h) => h.trim())
    .filter(Boolean);
  const blockedReason = item.blockedReason?.trim() || undefined;
  const deferReason = item.deferReason?.trim() || undefined;
  const dependsOnRefs = showMachinery
    ? (item.dependsOnRefs ?? []).map((r) => r.trim()).filter(Boolean)
    : [];
  const issueSummary = item.issueSummary?.trim() || undefined;
  const assignee = showMachinery
    ? exoAssigneeLabel(item)
    : exoClientDisplayName(exoAssigneeLabel(item));

  const actorKindLabel = item.actorMarker
    ? item.actorMarker.kind === 'agent'
      ? 'Agent'
      : 'Human'
    : undefined;
  const actorNameRaw = item.actorMarker?.label?.trim() || undefined;
  const actorName = actorNameRaw
    ? showMachinery
      ? actorNameRaw
      : exoClientDisplayName(actorNameRaw)
    : undefined;
  const effort = exoClientDisplayName(item.estimatedDuration);
  const waypointLabel = exoClientDisplayName(item.waypointRef);
  const actorDisplay =
    (actorName && actorName !== actorKindLabel ? actorName : undefined) ||
    (assignee && assignee !== actorKindLabel ? assignee : undefined);

  return {
    metaLine: showMachinery
      ? metaParts.length > 0
        ? metaParts.join(' · ')
        : 'Eligible for execution in the current scope.'
      : metaParts.join(' · '),
    assignee,
    evidence,
    description,
    acceptanceHints,
    blockedReason,
    deferReason,
    dependsOnRefs,
    issueSummary,
    actorKindLabel,
    actorName,
    actorDisplay,
    effort,
    waypointLabel,
    recordContext,
  };
}

/** Flat context string (queue tooling / legacy). Prefer exoHeroContext in the hero UI. */
export function exoContextText(item: ControlBridgeQueueItem): string {
  const ctx = exoHeroContext(item);
  const parts: string[] = [ctx.metaLine];
  if (ctx.assignee) parts.push(ctx.assignee);
  if (ctx.evidence) parts.push(ctx.evidence);
  if (ctx.recordContext) {
    const rc = ctx.recordContext;
    if (rc.name) parts.push(rc.name);
    if (rc.customer) parts.push(rc.customer);
    if (rc.value) parts.push(rc.value);
    if (rc.ageAtStep) parts.push(`Age ${rc.ageAtStep}`);
    if (rc.lastTouch) parts.push(`Touch ${rc.lastTouch}`);
    if (rc.owner) parts.push(rc.owner);
  } else if (ctx.description) {
    parts.push(ctx.description);
  }
  if (ctx.acceptanceHints.length) parts.push(`Accept: ${ctx.acceptanceHints.join('; ')}`);
  if (ctx.blockedReason) parts.push(`Blocked: ${ctx.blockedReason}`);
  if (ctx.deferReason) parts.push(`Deferred: ${ctx.deferReason}`);
  if (ctx.dependsOnRefs.length) parts.push(`Depends on: ${ctx.dependsOnRefs.join(', ')}`);
  if (ctx.issueSummary) parts.push(ctx.issueSummary);
  return parts.join(' · ');
}

/** Tracked artifact id for approval-card Change patches. */
export function exoApprovalTrackedItemId(item: ControlBridgeQueueItem): string | undefined {
  return (
    item.responseSchemaTargetRef?.trim() ||
    item.recordContext?.trackedArtifactRef?.trim() ||
    undefined
  );
}

/** Plain-language queue position for binary approval cards. */
export function exoApprovalBreadcrumb(
  item: ControlBridgeQueueItem,
  selectedIndex: number,
  queueLength: number,
  scopeLabel?: string
): string {
  const area =
    exoClientDisplayName(item.filterScope) ||
    exoClientDisplayName(item.domain) ||
    exoClientDisplayName(item.improvementTitle) ||
    scopeLabel?.trim() ||
    'Queue';
  const n = selectedIndex >= 0 ? selectedIndex + 1 : 1;
  const m = Math.max(1, queueLength);
  return `${area} · ${n} of ${m}`;
}

export function exoQueueRowSummary(item: ControlBridgeQueueItem): string {
  const action = exoHeroActionLine(item);
  return action ? `${item.title} — ${action}` : item.title;
}

/** Remaining queue rows besides the hero. Count and list share this source (B5). */
export function exoUpNextItems(
  queue: ControlBridgeQueueItem[],
  selectedId: string | null | undefined
): ControlBridgeQueueItem[] {
  return queue.filter((item) => !itemIdMatchesSource(selectedId, item));
}

/** Client-safe Chat shelf fields for a selected CB item (FE-CHAT-4). No ids. */
export interface ExoChatSelectedWork {
  title: string;
  actor?: string;
  stage?: string;
  canOpenInExo: boolean;
}

export function exoChatSelectedWork(item: ControlBridgeQueueItem): ExoChatSelectedWork {
  const ctx = exoHeroContext(item);
  const actor =
    [ctx.actorKindLabel, ctx.actorDisplay].filter(Boolean).join(' ').trim() || undefined;
  const status = item.statusPhrase?.trim();
  const statusLooksLikeSm = Boolean(status && /^[A-Z][A-Z0-9_]{2,}$/.test(status));
  const stage = ctx.waypointLabel || (!statusLooksLikeSm ? status : undefined);
  const rawTitle = item.title?.trim() || '';
  const title = rawTitle && !looksLikeKryspaceId(rawTitle) ? rawTitle : 'Selected work';
  return {
    title,
    actor: actor || undefined,
    stage,
    canOpenInExo: item.requiresBridgeSession !== true,
  };
}

/** Activity-lifecycle soft labels (hero action / success copy). */
const SOFT_STATE: Record<string, string> = {
  CREATED: 'Not started',
  INITIALIZED: 'Not started',
  ACTIVE: 'In progress',
  CLOSED: 'Done',
  COMPLETE: 'Done',
  VALIDATED: 'Validated',
  IDENTIFIED: 'Identified',
};

/** Tracked-artifact hop labels — keep operational state names readable. */
const TRACKED_HOP_STATE: Record<string, string> = {
  IDENTIFIED: 'Identified',
  DEFINED: 'Defined',
  VALIDATED: 'Validated',
  CREATED: 'Created',
  INITIALIZED: 'Initialized',
  DRAFTED: 'Drafted',
  ACTIVE: 'Active',
  CLOSED: 'Done',
  COMPLETE: 'Done',
  RETIRED: 'Retired',
  SUPERSEDED: 'Superseded',
};

function titleCaseStateToken(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');
}

export function exoSoftStateLabel(raw: string | undefined): string | undefined {
  const s = raw?.trim();
  if (!s) return undefined;
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(s)) {
    return SOFT_STATE[s] ?? titleCaseStateToken(s);
  }
  return s;
}

/** Soft label for tracked-record state hops (never activity "Not started"). */
export function exoTrackedHopStateLabel(raw: string | undefined): string | undefined {
  const s = raw?.trim();
  if (!s) return undefined;
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(s)) {
    return TRACKED_HOP_STATE[s] ?? titleCaseStateToken(s);
  }
  return s;
}

/** Drop error codes / persist-mode tokens from operator-facing Exo copy. */
export function exoSoftOperatorMessage(raw: string | undefined): string {
  const text = (raw ?? '').trim();
  if (!text) return 'That action did not finish.';
  if (/PERSIST_MODE|persistMode/i.test(text)) {
    return 'The change could not be saved in this session. Try again.';
  }
  if (/responseSchemaTargetRef/i.test(text)) {
    return 'There is no record to write this answer on.';
  }
  if (/Workspace slug/i.test(text)) {
    return 'This workspace is not ready to save yet.';
  }
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(text)) {
    return 'This item cannot be answered here yet.';
  }
  const stripped = text
    .replace(/\s*\([A-Z][A-Z0-9-]*\)\s*/g, ' ')
    .replace(/\s*Flag to KRYBOT\.?/gi, ' ')
    .replace(/^This activity cannot (?:Complete|Approve):\s*/i, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+\./g, '.')
    .replace(/\.{2,}/g, '.')
    .trim();
  return stripped || 'That action did not finish.';
}

export function exoCardEyebrow(kind: 'approval' | 'instance' | 'set'): string {
  if (kind === 'set') return 'Improvement';
  if (kind === 'approval') return 'Decision';
  return 'Record';
}

/** Tracked-record next step — never the activity lifecycle name. */
export function exoTrackedTransitionLine(item: ControlBridgeQueueItem): string | undefined {
  const name = exoClientDisplayName(item.recordContext?.name);
  const improvement = exoClientDisplayName(item.improvementTitle);
  const fromRaw =
    item.transitionContract?.transition?.fromState ||
    item.transitionContract?.trackedCurrentState;
  const toRaw =
    item.transitionContract?.transition?.toState || item.responseSchemaTargetState;
  const from = fromRaw ? exoTrackedHopStateLabel(fromRaw) : undefined;
  const next = toRaw ? exoTrackedHopStateLabel(toRaw) : undefined;
  const hop =
    from && next && from !== next
      ? `${from} → ${next}`
      : next
        ? `next: ${next}`
        : undefined;
  if (!hop) return undefined;
  if (name) return `${name} — ${hop}`;
  if (improvement) return `${improvement} — ${hop}`;
  return hop.startsWith('next:') ? `Next:${hop.slice(5)}` : hop;
}

export function formatExoTodayLabel(isoDate = new Date()): string {
  return isoDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatExoCalendarDayLabel(isoDate: string): string {
  const parsed = new Date(`${isoDate.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Gated transition (requiresBridgeSession false) opens Exo. Judgment opens Chat. */
export function exoMessageRoute(
  item: Pick<ControlBridgeQueueItem, 'itemId' | 'blockerClass' | 'requiresBridgeSession'>
) {
  const judgment = item.requiresBridgeSession === true;
  const gated = item.requiresBridgeSession === false;
  return resolveClickTarget({
    itemId: item.itemId,
    blockerClass: item.blockerClass,
    closable: !judgment,
    forceRoute: gated ? 'exo' : judgment ? 'chat' : undefined,
  });
}
